// src/utils/auth.js
import { get, post } from './http';

const KEY_TOKEN   = 'web3school_token';
const KEY_USER    = 'web3school_user';
const KEY_EXP     = 'web3school_exp'; // 秒级时间戳（后端返回的 expires_at）

/** ========== 本地存取工具 ========== */
function saveToken(token, expSec = null) {
  localStorage.setItem(KEY_TOKEN, token);
  if (typeof expSec === 'number') {
    localStorage.setItem(KEY_EXP, String(expSec));
  }
}
function clearToken() {
  localStorage.removeItem(KEY_TOKEN);
  localStorage.removeItem(KEY_EXP);
}
function saveUser(profile) {
  if (profile) localStorage.setItem(KEY_USER, JSON.stringify(profile));
}
function clearUser() {
  localStorage.removeItem(KEY_USER);
}
function nowSec() {
  return Math.floor(Date.now() / 1000);
}

/** ========== 对外工具（同步） ========== */
export function isAuthed() {
  try {
    const token = localStorage.getItem(KEY_TOKEN);
    if (!token) return false;
    const expStr = localStorage.getItem(KEY_EXP);
    if (!expStr) return true; // 兼容旧数据：没有过期时间就当有效
    const exp = Number(expStr);
    if (!Number.isFinite(exp)) return true;
    if (nowSec() >= exp) {
      // 过期自动登出
      logout();
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function currentUser() {
  try { return JSON.parse(localStorage.getItem(KEY_USER) || 'null'); } catch { return null; }
}

// 可选：让别处也能直接写入本地用户（如 /me 刷新）
export function setCurrentUser(profile) {
  saveUser(profile);
  window.dispatchEvent(new Event('auth-changed'));
}

/** ========== API 封装（异步） ========== */

/** 注册 */
export async function apiRegister(payload /* { name, email, password } */) {
  const data = await post('/api/auth/register', payload);
  // 成功：data.ok, data.user
  // 注册一般不直接发 token，这里只缓存 user，交由登录获取 token
  if (data?.user) saveUser(data.user);
  return data;
}

/** 登录 */
export async function apiLogin(payload /* { email, password } */) {
  const data = await post('/api/auth/login', payload);
  // { ok, token_type, access_token, expires_at, user }
  if (data?.access_token) {
    saveToken(data.access_token, data?.expires_at);
  }
  if (data?.user) {
    saveUser(data.user);
  }
  // 通知全站
  window.dispatchEvent(new Event('auth-changed'));
  return data;
}

/** 获取个人资料 */
export async function apiGetMe() {
  const data = await get('/api/auth/me');
  if (data?.user) {
    saveUser(data.user);
    // 仅刷新用户信息；token 有效性由 isAuthed() 判定
    window.dispatchEvent(new Event('auth-changed'));
  }
  return data;
}

/** 供组件在挂载时确保本地资料最新（可选） */
export async function ensureProfileLoaded() {
  if (!isAuthed()) return null;
  const u = currentUser();
  if (u) return u;
  try {
    const me = await apiGetMe();
    return me?.user || null;
  } catch {
    // token 可能失效
    logout();
    return null;
  }
}

/** 登出 */
export function logout() {
  clearToken();
  clearUser();
  window.dispatchEvent(new Event('auth-changed'));
}

/** ========== 最少改动新增：忘記/重設 密碼 ========== */

/** 忘記密碼：POST /api/auth/forgot  { email } */
export async function apiForgot(email) {
  return await post('/api/auth/forgot', { email });
}

/** 重設密碼：POST /api/auth/reset  { token, password } */
export async function apiReset({ token, password }) {
  return await post('/api/auth/reset', { token, password });
}
